<template>
  <span v-if="showEmpty" class="status-pill-empty">—</span>
  <span
    v-else
    class="status-pill"
    :class="pillClass"
  >
    <v-icon
      v-if="pillIcon && isMdiIcon"
      :icon="pillIcon"
      class="status-pill-mdi-icon"
      size="14"
    />
    <DpplSvg
      v-else-if="pillIcon"
      class="status-pill-icon"
      :file-name="pillIcon"
    />
    {{ label || '—' }}
  </span>
</template>

<script setup>
import { computed } from 'vue';
import DpplSvg from '@/components/datapeople/DpplSvg.vue';
import { FLSA_ALERT_ICONS } from '@/components/library/flsaSurveys/helpers';
import { ACKNOWLEDGEMENT_ALERT_ICONS } from '@/helpers/acknowledgementAlerts';

const props = defineProps({
  statusKey: { type: String, default: null },
  label: { type: String, default: '' },
  variant: { type: String, default: null },
  icon: { type: String, default: null },
  iconType: { type: String, default: 'svg' },
});

const isMdiIcon = computed(() => props.iconType === 'mdi');

const pillIcon = computed(() => {
  if (props.icon) {
    return props.icon;
  }

  return FLSA_ALERT_ICONS[props.statusKey]
    || ACKNOWLEDGEMENT_ALERT_ICONS[props.statusKey]
    || null;
});

const pillClass = computed(() => {
  if (props.variant) {
    return props.variant;
  }

  const key = props.statusKey;
  const map = {
    no_acknowledgement: 'no-acknowledgement',
    pending_acknowledgement: 'incomplete',
    job_profile_id_changed: 'job-profile-changed',
    job_profile_content_changed: 'job-profile-changed',
    acknowledgement_out_of_date: 'out-of-date',
    acknowledgement_ok: 'completed',
    cancelled: 'cancelled',
    inactive: 'inactive',
    architecture_alert: 'architecture-alert',
    no_flsa_submission: 'flsa-no-submission',
    outdated_form: 'flsa-outdated-form',
    status_mismatch: 'flsa-status-mismatch',
    review_overdue: 'flsa-review-overdue',
    profile_change: 'job-profile-changed',
    up_to_date: 'flsa-up-to-date',
    multiple: 'multiple-alerts',
    flsa_federal: 'flsa-federal',
    flsa_washington: 'flsa-federal',
    flsa_california: 'flsa-california',
    flsa_new_york: 'flsa-new-york',
    flsa_alaska: 'flsa-alaska',
    flsa_colorado: 'flsa-brand',
    flsa_massachusetts: 'flsa-brand',
    flsa_default: 'flsa-default',
  };

  return map[key] || null;
});

const showEmpty = computed(() => {
  if (props.variant) {
    return !props.variant;
  }

  if (!props.statusKey) {
    return true;
  }

  return !pillClass.value;
});
</script>

<style lang="less" scoped>
.status-pill-empty {
  color: @text-light;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: fit-content;
  padding: 0 8px;
  height: 24px;
  max-height: 24px;
  border-radius: 50px;
  border: 1px solid transparent;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
  box-sizing: border-box;

  .status-pill-icon,
  .status-pill-mdi-icon {
    flex-shrink: 0;
  }

  .status-pill-icon {
    width: 16px;
    height: 16px;

    :deep(path) {
      fill: currentColor;
    }
  }

  .status-pill-mdi-icon {
    width: 14px;
    height: 14px;
  }

  &.no-acknowledgement,
  &.incomplete,
  &.job-profile-changed,
  &.out-of-date,
  &.completed {
    height: 20px;
    max-height: 20px;
    gap: 2px;
    border: none;

    .status-pill-icon {
      width: 14px;
      height: 14px;
    }
  }

  &.no-acknowledgement {
    background: @red-100;
    color: @red-700;
  }

  &.incomplete {
    background: @grey-300;
    color: @grey-800;
  }

  &.job-profile-changed {
    background: @navy-100;
    color: @navy-700;
  }

  &.architecture-alert {
    background: @yellow-100;
    color: @yellow-700;
    border-color: @yellow-300;
  }

  &.out-of-date {
    background: @yellow-100;
    color: @yellow-700;
  }

  &.completed {
    background: @teal-100;
    color: @teal-500;
  }

  &.cancelled, &.inactive {
    background: @grey-200;
    color: @text-light;
    border-color: @grey-500;
  }

  &.multiple-alerts {
    background: @indigo-100;
    color: @indigo-700;
    border: none;
  }

  &.flsa-up-to-date {
    background: @teal-100;
    color: @teal-700;
    border: none;
  }

  &.flsa-no-submission {
    background: @red-100;
    color: @red-700;
    border: none;
  }

  &.flsa-outdated-form {
    background: @purple-100;
    color: @purple-500;
    border: none;
  }

  &.flsa-status-mismatch {
    background: @pink-100;
    color: @pink-700;
    border: none;
  }

  &.flsa-review-overdue {
    background: @yellow-100;
    color: @yellow-700;
    border: none;
  }

  &.flsa-federal,
  &.flsa-california,
  &.flsa-new-york,
  &.flsa-alaska,
  &.flsa-brand,
  &.flsa-default {
    height: 22px;
    max-height: 22px;
    padding: 2px 8px;
    color: @text-white;
  }

  &.flsa-federal {
    background: @brand-primary;
    border-color: @brand-primary;
  }

  &.flsa-california {
    background: @orange-500;
    border-color: @orange-500;
  }

  &.flsa-new-york {
    background: @yellow-500;
    border-color: @yellow-500;
  }

  &.flsa-alaska {
    background: @success;
    border-color: @success;
  }

  &.flsa-brand {
    background: @text-brand;
    border-color: @text-brand;
  }

  &.flsa-default {
    background: @grey-400;
    border-color: @grey-400;
    color: @text-darkest;
  }

  &.approval-pending,
  &.approval-approved,
  &.approval-rejected,
  &.approval-cancelled {
    height: 20px;
    max-height: 20px;
    padding: 2px 8px;
    gap: 5px;
    border: none;
    line-height: 16.5px;
  }

  &.approval-pending {
    background: @yellow-100;
    color: @yellow-700;
  }

  &.approval-approved {
    background: @teal-100;
    color: @teal-700;
  }

  &.approval-rejected {
    background: @red-100;
    color: @red-700;
  }

  &.approval-cancelled {
    background: @grey-400;
    color: @grey-800;
  }
}
</style>
